import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import menuPreferencesService from '../services/menuPreferencesService';

const MenuPreferencesContext = createContext();

// Chave do localStorage
const STORAGE_KEY = 'vidal_menu_preferences';

// Helpers do localStorage
const saveToLocalStorage = (userId, projectId, preferences) => {
  try {
    const key = `${STORAGE_KEY}_${userId}_${projectId}`;
    localStorage.setItem(key, JSON.stringify({
      preferences,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Erro ao salvar no localStorage:', error);
  }
};

const loadFromLocalStorage = (userId, projectId) => {
  try {
    const key = `${STORAGE_KEY}_${userId}_${projectId}`;
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Erro ao carregar do localStorage:', error);
    return null;
  }
};

// Preferências padrão (mesmas do backend)
const PREFERENCES_VERSION = 15; // Incrementar quando adicionar novos itens

const DEFAULT_PREFERENCES = {
  version: PREFERENCES_VERSION,
  categories: [
    { id: 'favorites', name: 'Favoritos', order: 0, isSystem: true, collapsed: false },
    { id: 'dashboards', name: 'Dashboards', order: 1, isSystem: false, collapsed: false },
    { id: 'statistics', name: 'Estatísticas', order: 2, isSystem: false, collapsed: false },
    { id: 'tools', name: 'Ferramentas', order: 3, isSystem: false, collapsed: false },
    { id: 'settings', name: 'Configurações', order: 4, isSystem: false, collapsed: false },
    { id: 'hidden', name: 'Ocultos', order: 999, isSystem: true, collapsed: true }
  ],
  items: [
    { id: 'files', categoryId: 'dashboards', order: 0, isFavorite: false, isHidden: false },
    { id: 'transactions', categoryId: 'dashboards', order: 1, isFavorite: false, isHidden: false },
    { id: 'notificacoes-fiscais', categoryId: 'dashboards', order: 2, isFavorite: false, isHidden: false },
    { id: 'products', categoryId: 'statistics', order: 0, isFavorite: false, isHidden: false },
    { id: 'clients', categoryId: 'statistics', order: 1, isFavorite: false, isHidden: false },
    { id: 'suppliers', categoryId: 'statistics', order: 2, isFavorite: false, isHidden: false },
    { id: 'reforma-tributaria', categoryId: 'statistics', order: 3, isFavorite: false, isHidden: false },
    { id: 'validacao-fiscal', categoryId: 'statistics', order: 4, isFavorite: false, isHidden: false },
    { id: 'plcom', categoryId: 'statistics', order: 5, isFavorite: false, isHidden: false },
    { id: 'sped-reviewer', categoryId: 'tools', order: 0, isFavorite: false, isHidden: false },
    { id: 'downloads', categoryId: 'tools', order: 1, isFavorite: false, isHidden: false },
    { id: 'certificates', categoryId: 'tools', order: 2, isFavorite: false, isHidden: false },
    { id: 'xml-scan', categoryId: 'tools', order: 3, isFavorite: false, isHidden: false },
    { id: 'creditos', categoryId: 'tools', order: 4, isFavorite: false, isHidden: false },
    { id: 'nfe-downloader', categoryId: 'tools', order: 5, isFavorite: false, isHidden: false },
    { id: 'lead-scraper', categoryId: 'tools', order: 6, isFavorite: false, isHidden: false },
    { id: 'email-marketing', categoryId: 'tools', order: 7, isFavorite: false, isHidden: false },
    { id: 'automec-fornecedores', categoryId: 'tools', order: 8, isFavorite: false, isHidden: false },
    { id: 'admin', categoryId: 'settings', order: 0, isFavorite: false, isHidden: false },
    { id: 'activity-logs', categoryId: 'settings', order: 1, isFavorite: false, isHidden: false },
    { id: 'projects', categoryId: 'settings', order: 2, isFavorite: false, isHidden: false },
    { id: 'users', categoryId: 'settings', order: 3, isFavorite: false, isHidden: false }
  ]
};

export const MenuPreferencesProvider = ({ children }) => {
  const auth = useAuth();
  const { currentProject, user } = auth;
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const previousProjectIdRef = useRef(null);
  const authRef = useRef(auth);
  
  // Atualizar ref do auth
  useEffect(() => {
    authRef.current = auth;
  }, [auth]);

  /**
   * Migra preferências antigas para o formato atual
   * Remove itens obsoletos e adiciona novos itens padrão que não existem
   */
  const migratePreferences = (prefs) => {
    const OBSOLETE_ITEMS = ['sped-download', 'xml-download', 'tax-monitoring', 'dec', 'billing'];
    let items = prefs.items.filter(i => !OBSOLETE_ITEMS.includes(i.id));

    // Adicionar itens padrão que não existem nas preferências carregadas
    DEFAULT_PREFERENCES.items.forEach(defaultItem => {
      if (!items.find(i => i.id === defaultItem.id)) {
        items.push({ ...defaultItem });
      }
    });

    return { ...prefs, version: PREFERENCES_VERSION, items };
  };

  /**
   * Carrega preferências - localStorage primeiro, depois sincroniza com backend
   */
  const loadPreferences = useCallback(async () => {
    const projectId = currentProject?.cnpj || currentProject?.nome;
    const userId = user?.id;
    
    if (!projectId || !userId) {
      setPreferences(DEFAULT_PREFERENCES);
      return;
    }

    setLoading(true);
    
    // 1. Carregar do localStorage primeiro (instantâneo)
    const localData = loadFromLocalStorage(userId, projectId);
    
    // Verificar versão - se desatualizada, migrar para adicionar novos itens
    if (localData?.preferences) {
      const migrated = migratePreferences(localData.preferences);
      setPreferences(migrated);
      saveToLocalStorage(userId, projectId, migrated);
    } else {
      setPreferences(DEFAULT_PREFERENCES);
      saveToLocalStorage(userId, projectId, DEFAULT_PREFERENCES);
    }
    
    setLoading(false);

    // 2. Sincronizar com backend em background
    try {
      const result = await menuPreferencesService.getPreferences(projectId);
      
      if (result.success && result.preferences) {
        // Migrar preferências antigas: remover itens obsoletos e adicionar novos
        const migrated = migratePreferences(result.preferences);
        setPreferences(migrated);
        saveToLocalStorage(userId, projectId, migrated);
      } else if (!localData) {
        saveToLocalStorage(userId, projectId, DEFAULT_PREFERENCES);
      }
    } catch (error) {
      // Mantém dados do localStorage se backend falhar
    }
  }, [currentProject, user]);

  /**
   * Salva preferências imediatamente (localStorage + backend)
   */
  const savePreferences = useCallback(async (newPreferences) => {
    const { user: currentUser, currentProject: currentProj } = authRef.current;
    const userId = currentUser?.id;
    const projectId = currentProj?.cnpj || currentProj?.nome;
    
    if (!projectId || !userId) {
      return;
    }

    // 1. Salvar no localStorage imediatamente (síncrono)
    saveToLocalStorage(userId, projectId, newPreferences);

    // 2. Salvar no backend (assíncrono, sem bloquear UI)
    setSaving(true);
    try {
      await menuPreferencesService.savePreferences(projectId, newPreferences);
    } catch (error) {
      // localStorage já foi salvo, então usuário não perde dados
    } finally {
      setSaving(false);
    }
  }, []);

  /**
   * Atualiza preferências localmente e agenda save
   */
  const updatePreferences = useCallback((newPreferences) => {
    setPreferences(newPreferences);
    savePreferences(newPreferences);
  }, [savePreferences]);

  /**
   * Toggle modo de edição
   */
  const toggleEditMode = useCallback(() => {
    setEditMode(prev => !prev);
  }, []);

  /**
   * Reseta preferências para o padrão
   */
  const resetPreferences = useCallback(async () => {
    const projectId = currentProject?.cnpj || currentProject?.nome;
    const userId = user?.id;
    
    if (!projectId || !userId) return;

    try {
      const result = await menuPreferencesService.resetPreferences(projectId);
      
      if (result.success) {
        setPreferences(result.preferences);
        
        // Atualizar localStorage com preferências padrão
        saveToLocalStorage(userId, projectId, result.preferences);
      }
    } catch (error) {
      console.error('Erro ao resetar preferências:', error);
    }
  }, [currentProject, user]);

  /**
   * Restaura todos os itens para suas categorias originais
   */
  const resetAllItemsToDefault = useCallback(() => {
    const newPreferences = {
      ...preferences,
      items: DEFAULT_PREFERENCES.items.map(defaultItem => {
        // Mantém o item existente mas restaura categoria, favorito e oculto
        const existingItem = preferences.items.find(i => i.id === defaultItem.id);
        return {
          ...existingItem,
          categoryId: defaultItem.categoryId,
          order: defaultItem.order,
          isFavorite: false,
          isHidden: false
        };
      })
    };

    updatePreferences(newPreferences);
  }, [preferences, updatePreferences]);

  /**
   * Adiciona nova categoria
   */
  const addCategory = useCallback((categoryName) => {
    const newCategory = {
      id: `custom_${Date.now()}`,
      name: categoryName,
      order: preferences.categories.length - 1, // Antes de "Ocultos"
      isSystem: false,
      collapsed: false
    };

    const newPreferences = {
      ...preferences,
      categories: [
        ...preferences.categories.filter(c => c.id !== 'hidden'),
        newCategory,
        preferences.categories.find(c => c.id === 'hidden')
      ].map((cat, idx) => ({ ...cat, order: idx }))
    };

    updatePreferences(newPreferences);
  }, [preferences, updatePreferences]);

  /**
   * Remove categoria (itens voltam para categoria padrão)
   */
  const removeCategory = useCallback((categoryId) => {
    if (preferences.categories.find(c => c.id === categoryId)?.isSystem) {
      console.warn('Não é possível remover categorias do sistema');
      return;
    }

    const defaultCategoryId = 'dashboards';
    
    const newPreferences = {
      categories: preferences.categories
        .filter(c => c.id !== categoryId)
        .map((cat, idx) => ({ ...cat, order: idx })),
      items: preferences.items.map(item => 
        item.categoryId === categoryId 
          ? { ...item, categoryId: defaultCategoryId }
          : item
      )
    };

    updatePreferences(newPreferences);
  }, [preferences, updatePreferences]);

  /**
   * Renomeia categoria
   */
  const renameCategory = useCallback((categoryId, newName) => {
    const newPreferences = {
      ...preferences,
      categories: preferences.categories.map(cat =>
        cat.id === categoryId ? { ...cat, name: newName } : cat
      )
    };

    updatePreferences(newPreferences);
  }, [preferences, updatePreferences]);

  /**
   * Toggle collapse de categoria
   */
  const toggleCategoryCollapse = useCallback((categoryId) => {
    const newPreferences = {
      ...preferences,
      categories: preferences.categories.map(cat =>
        cat.id === categoryId ? { ...cat, collapsed: !cat.collapsed } : cat
      )
    };

    updatePreferences(newPreferences);
  }, [preferences, updatePreferences]);

  /**
   * Move item para outra categoria
   */
  const moveItemToCategory = useCallback((itemId, newCategoryId) => {
    const newPreferences = {
      ...preferences,
      items: preferences.items.map(item =>
        item.id === itemId ? { ...item, categoryId: newCategoryId } : item
      )
    };

    updatePreferences(newPreferences);
  }, [preferences, updatePreferences]);

  /**
   * Toggle favorito
   */
  const toggleFavorite = useCallback((itemId) => {
    const item = preferences.items.find(i => i.id === itemId);
    const isFavorite = !item.isFavorite;
    
    // Buscar categoria original do item no DEFAULT_PREFERENCES
    const defaultItem = DEFAULT_PREFERENCES.items.find(i => i.id === itemId);
    const originalCategoryId = defaultItem?.categoryId || 'dashboards';

    const newPreferences = {
      ...preferences,
      items: preferences.items.map(i =>
        i.id === itemId 
          ? { 
              ...i, 
              isFavorite,
              categoryId: isFavorite ? 'favorites' : originalCategoryId, // Volta para categoria original
              isHidden: false // Remove de ocultos se estava lá
            }
          : i
      )
    };

    updatePreferences(newPreferences);
  }, [preferences, updatePreferences]);

  /**
   * Toggle oculto
   */
  const toggleHidden = useCallback((itemId) => {
    const item = preferences.items.find(i => i.id === itemId);
    const isHidden = !item.isHidden;
    
    // Buscar categoria original do item no DEFAULT_PREFERENCES
    const defaultItem = DEFAULT_PREFERENCES.items.find(i => i.id === itemId);
    const originalCategoryId = defaultItem?.categoryId || 'dashboards';

    const newPreferences = {
      ...preferences,
      items: preferences.items.map(i =>
        i.id === itemId 
          ? { 
              ...i, 
              isHidden,
              categoryId: isHidden ? 'hidden' : originalCategoryId, // Volta para categoria original
              isFavorite: false // Remove de favoritos se estava lá
            }
          : i
      )
    };

    updatePreferences(newPreferences);
  }, [preferences, updatePreferences]);

  /**
   * Reordena itens dentro de uma categoria
   */
  const reorderItems = useCallback((categoryId, itemIds) => {
    const newPreferences = {
      ...preferences,
      items: preferences.items.map(item => {
        if (item.categoryId === categoryId) {
          const newOrder = itemIds.indexOf(item.id);
          return newOrder >= 0 ? { ...item, order: newOrder } : item;
        }
        return item;
      })
    };

    updatePreferences(newPreferences);
  }, [preferences, updatePreferences]);

  /**
   * Reordena categorias
   */
  const reorderCategories = useCallback((categoryIds) => {
    const newPreferences = {
      ...preferences,
      categories: preferences.categories.map(cat => {
        const newOrder = categoryIds.indexOf(cat.id);
        return newOrder >= 0 ? { ...cat, order: newOrder } : cat;
      }).sort((a, b) => a.order - b.order)
    };

    updatePreferences(newPreferences);
  }, [preferences, updatePreferences]);

  // Efeito para carregar preferências quando projeto muda
  useEffect(() => {
    const projectId = currentProject?.cnpj || currentProject?.nome;
    
    // Só recarrega se o projeto realmente mudou
    if (projectId && projectId !== previousProjectIdRef.current) {
      previousProjectIdRef.current = projectId;
      loadPreferences();
      setEditMode(false); // Desativa modo de edição ao trocar projeto
    }
  }, [currentProject, loadPreferences]);

  return (
    <MenuPreferencesContext.Provider
      value={{
        preferences,
        editMode,
        loading,
        saving,
        toggleEditMode,
        updatePreferences,
        resetPreferences,
        resetAllItemsToDefault,
        addCategory,
        removeCategory,
        renameCategory,
        toggleCategoryCollapse,
        moveItemToCategory,
        toggleFavorite,
        toggleHidden,
        reorderItems,
        reorderCategories,
        loadPreferences
      }}
    >
      {children}
    </MenuPreferencesContext.Provider>
  );
};

export const useMenuPreferences = () => {
  const context = useContext(MenuPreferencesContext);
  if (!context) {
    throw new Error('useMenuPreferences must be used within a MenuPreferencesProvider');
  }
  return context;
};
